import React, { useState , useEffect, useMemo, useCallback } from 'react';
import swal from '@sweetalert/with-react';
import { API } from "aws-amplify";

import { useDebouncedValue, sortArrayOfObjects, deleteSection, calculateConflicts } from '../../../js/Helpers';
import { getPeriodNameFromCoursePeriodId } from '../../../js/PeriodHelpers';
import { getLabelColorClass } from '../utils/labelColorUtils';
import { NO_DEPARTMENT_FILTER_VALUE, prependNoDepartmentFilterOption } from '../utils/departmentFilterUtils';
import { getSectionTermFilterInfo, getSectionTermValues, getTermFilterOptionsFromSections } from '../utils/sectionTermUtils';

import SortToggle from '../../SortToggle';
import SchoolDataFilter, { applySchoolDataFilters } from '../../SchoolDataFilter';
import EditSection from '../modals/edit_section';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faEdit, faPlus, faLock, faUnlock, faMinusSquare, faSortDown, faCheckSquare, faUserGraduate, faClone } from '@fortawesome/free-solid-svg-icons';
import { faTrashAlt, faSquare as farSquare } from '@fortawesome/free-regular-svg-icons';
import { useConflictActions } from '../store/magnetboardStore';

const hasCourseId = (course_id) => course_id !== null && course_id !== undefined && `${course_id}` !== '';
const isSectionLocked = (section_info) => `${section_info?.locked}` === '1';
const UNPLACED_FILTER_VALUE = 'unplaced';

const getTermDisplayInfo = (section_info) => {
	const selected_terms = getSectionTermValues(section_info);
	const uses_cycle_terms = selected_terms.some(term => ['5', '6'].includes(term));
	const term_prefix = uses_cycle_terms ? 'C' : 'Q';
	const display_terms = uses_cycle_terms ? ['1', '2', '3', '4', '5', '6'] : ['1', '2', '3', '4'];

	return {selected_terms, term_prefix, display_terms};
};

const compareSectionTermSort = (section_a, section_b, sort_direction = 'asc') => {
	const term_count_a = Number.isFinite(section_a?.term_quarter_count_sort_value) ? section_a.term_quarter_count_sort_value : 0;
	const term_count_b = Number.isFinite(section_b?.term_quarter_count_sort_value) ? section_b.term_quarter_count_sort_value : 0;
	const unplaced_a = term_count_a === 0;
	const unplaced_b = term_count_b === 0;
	const direction_modifier = sort_direction === 'desc' ? -1 : 1;

	if(unplaced_a !== unplaced_b) return unplaced_a ? 1 : -1;
	if(term_count_a !== term_count_b)
	{
		return sort_direction === 'desc' ? term_count_b - term_count_a : term_count_a - term_count_b;
	}

	const term_sequence_compare = `${section_a?.term_sequence_sort_text || ''}`.localeCompare(`${section_b?.term_sequence_sort_text || ''}`, undefined, {numeric:true, sensitivity:'base'});
	if(term_sequence_compare !== 0) return term_sequence_compare * direction_modifier;

	const course_code_compare = `${section_a?.course_code || ''}`.localeCompare(`${section_b?.course_code || ''}`, undefined, {numeric:true, sensitivity:'base'});
	if(course_code_compare !== 0) return course_code_compare;

	const course_name_compare = `${section_a?.course_name || ''}`.localeCompare(`${section_b?.course_name || ''}`, undefined, {numeric:true, sensitivity:'base'});
	if(course_name_compare !== 0) return course_name_compare;

	const period_compare = `${section_a?.period_term_sort_text || ''}`.localeCompare(`${section_b?.period_term_sort_text || ''}`, undefined, {numeric:true, sensitivity:'base'});
	if(period_compare !== 0) return period_compare;

	return `${section_a?.section_number || ''}`.localeCompare(`${section_b?.section_number || ''}`, undefined, {numeric:true, sensitivity:'base'});
};

export default function DatabaseDataSections({ schoolInfo, databaseData, scheduleStructureData, scheduleInfo, courses, setCourses, teachers, setTeachers, classrooms, students, setStudents, departments, sections, setSections, subsections, setSubsections, labels, setLabels, lunchInfo, dataIsLoading, coursesLoading, readOnlyAddUpload = false, visibleDataFilter = null, studentOnlySectionEditing = false, departmentFilterOptions = departments, showDepartmentFilter = true}) {
	const [handleChangeComplete, setHandleChangeComplete] = useState(false);

	const [filteredResults, setFilteredResults] = useState([]);
	const [filterActive, setFilterActive] = useState(false);
	const [searchValue, setSearchValue] = useState('');
	const [deferredSearchValue, setDeferredSearchValue] = useState(searchValue);
	const debouncedSearchValue = useDebouncedValue(deferredSearchValue, 120);

	const [showEditDataModal, setShowEditDataModal] = useState(false);
	const [currentDataID, setCurrentDataID] = useState(null);
	const [selectedData, setSelectedData] = useState([]);
	const [lockingSectionIDs, setLockingSectionIDs] = useState([]);
	const [duplicatingSectionIDs, setDuplicatingSectionIDs] = useState([]);
	const [deletingSectionIDs, setDeletingSectionIDs] = useState([]);

	const [sortedBy, setSortedBy] = useState('course_name');
	const [sortAscDesc, setSortAscDesc] = useState('asc');

	const { setConflicts, setMatchPercent, markConflictDataFresh } = useConflictActions();
	const canDeleteSections = !studentOnlySectionEditing;
	const canLockSections = !studentOnlySectionEditing;
	const showRowControls = canDeleteSections || canLockSections;
	const visibleDatabaseData = useMemo(() => (
		typeof visibleDataFilter === 'function' ? databaseData.filter(visibleDataFilter) : databaseData
	), [databaseData, visibleDataFilter]);
	const departmentFilterOptionsWithNoDepartment = useMemo(() => prependNoDepartmentFilterOption(departmentFilterOptions), [departmentFilterOptions]);
	const coursesById = useMemo(() => new Map((Array.isArray(courses) ? courses : []).map(course => [`${course.course_id}`, course])), [courses]);
	const teachersById = useMemo(() => new Map((Array.isArray(teachers) ? teachers : []).map(teacher => [`${teacher.teacher_id}`, teacher])), [teachers]);
	const labelsById = useMemo(() => new Map((Array.isArray(labels) ? labels : []).map(label => [`${label.label_id}`, label])), [labels]);
	const sectionsById = useMemo(() => new Map((Array.isArray(sections) ? sections : []).map(section => [`${section.section_id}`, section])), [sections]);
	const subsectionsById = useMemo(() => new Map((Array.isArray(subsections) ? subsections : []).map(subsection => [`${subsection.subsection_id}`, subsection])), [subsections]);
	const subsectionIdsByMainSectionId = useMemo(() => {
		const results = new Map();
		(Array.isArray(subsections) ? subsections : []).forEach(subsection_info => {
			if(!subsection_info || !subsection_info.section_id || !subsection_info.subsection_id) return;
			const main_section_id = `${subsection_info.section_id}`;
			if(!results.has(main_section_id)) results.set(main_section_id, []);
			const subsection_ids = results.get(main_section_id);
			if(!subsection_ids.some(subsection_id => `${subsection_id}` === `${subsection_info.subsection_id}`)) subsection_ids.push(subsection_info.subsection_id);
		});
		return results;
	}, [subsections]);
	const classroomsById = useMemo(() => new Map((Array.isArray(classrooms) ? classrooms : []).map(classroom => [`${classroom.classroom_id}`, classroom])), [classrooms]);
	const departmentsById = useMemo(() => new Map((Array.isArray(departments) ? departments : []).map(department => [`${department.department_id}`, department])), [departments]);
	const spanSectionsById = useMemo(() => {
		const results = new Map();

		(Array.isArray(sections) ? sections : []).forEach(section => {
			if(!section || !section.span_id) return;
			if(!results.has(section.span_id)) results.set(section.span_id, []);
			results.get(section.span_id).push(section);
		});

		return results;
	}, [sections]);

	const getSectionPeriodDisplayInfo = useCallback((section_info) => {
		let course_periods = Array.isArray(section_info?.course_periods) ? [...section_info.course_periods] : [];
		const span_id = section_info?.span_id;

		if(span_id)
		{
			const spanned_sections = spanSectionsById.get(span_id) || [];
			for(const spanned_section of spanned_sections)
			{
				const spanned_section_course_periods = Array.isArray(spanned_section.course_periods) ? spanned_section.course_periods : [];
				for(const spanned_section_course_period_id of spanned_section_course_periods)
				{
					if(!course_periods.includes(spanned_section_course_period_id)) course_periods.push(spanned_section_course_period_id);
				}
			}
		}

		const period_names = course_periods.reduce((results, course_period_id) => {
			if(!course_period_id) return results;
			const period_name = getPeriodNameFromCoursePeriodId(scheduleStructureData, course_period_id, {
				schedule_type: scheduleInfo.schedule_type,
				current_day: '1',
			});
			if(period_name && !results.includes(period_name)) results.push(period_name);
			return results;
		}, []);

		return {
			period_ids: course_periods,
			period_names: period_names.sort((period_name_a, period_name_b) => `${period_name_a}`.localeCompare(`${period_name_b}`, undefined, {numeric:true})),
		};
	}, [scheduleInfo.schedule_type, scheduleStructureData, spanSectionsById]);

	const schoolDataFilterCategories = useMemo(() => {
		const categories = [];
		if(showDepartmentFilter)
		{
			categories.push({
				key: 'departments',
				title: 'Departments',
				dataKey: 'departments',
				noValueFilterValue: NO_DEPARTMENT_FILTER_VALUE,
				options: departmentFilterOptionsWithNoDepartment.map(department_info => ({
					value: department_info.department_id,
					display: department_info.department,
				})),
			});
		}

		const course_period_options = Object.keys(scheduleStructureData?.course_periods || {}).reduce((results, course_period_id) => {
			const period_name = getPeriodNameFromCoursePeriodId(scheduleStructureData, course_period_id, {
				schedule_type: scheduleInfo.schedule_type,
				current_day: '1',
			});
			if(!period_name) return results;
			results.push({value: course_period_id, display: period_name});
			return results;
		}, []).sort((period_a, period_b) => `${period_a.display}`.localeCompare(`${period_b.display}`, undefined, {numeric:true}));
		const period_filter_options = [
			{value: UNPLACED_FILTER_VALUE, display: 'Unplaced'},
			...course_period_options,
		];

		if(period_filter_options.length > 0)
		{
			categories.push({
				key: 'periods',
				title: 'Periods',
				matchRow: (section_info, selected_periods) => {
					const period_ids = getSectionPeriodDisplayInfo(section_info).period_ids.map(period_id => `${period_id}`);
					return selected_periods.some(selected_period => (
						`${selected_period}` === UNPLACED_FILTER_VALUE
							? period_ids.length === 0
							: period_ids.includes(`${selected_period}`)
					));
				},
				options: period_filter_options,
			});
		}

		const term_filter_options = getTermFilterOptionsFromSections(visibleDatabaseData);

		categories.push({
			key: 'terms',
			title: 'Term',
			matchRow: (section_info, selected_terms) => {
				const section_term_filter_value = getSectionTermFilterInfo(section_info).value;
				return selected_terms.some(selected_term => `${selected_term}` === `${section_term_filter_value}`);
			},
			options: term_filter_options,
		});

		return categories;
	}, [departmentFilterOptionsWithNoDepartment, getSectionPeriodDisplayInfo, scheduleInfo.schedule_type, scheduleStructureData, showDepartmentFilter, visibleDatabaseData]);

	const getTeacherDisplayInfo = useCallback((teacher_id) => {
		const teacher_info = teachersById.get(`${teacher_id}`);
		if(!teacher_info) return null;

		return {
			first_name: teacher_info.first_name,
			last_name: teacher_info.name,
			full_name: `${teacher_info.name}, ${teacher_info.first_name}`,
		};
	}, [teachersById]);

	const addExtraDataToSection = useCallback((section) => {
		const section_course_id = section.course_id;
		const course_info = coursesById.get(`${section_course_id}`) || null;
		const teacher_display_info = getTeacherDisplayInfo(section.teacher_id);
		const classroom_info = classroomsById.get(`${section.classroom_id}`) || null;
		const section_departments = Array.isArray(section.departments) ? section.departments : [];
		const department_sort_text = section_departments.map(department_id => {
			const department_info = departmentsById.get(`${department_id}`);
			return department_info ? department_info.department : 'No department';
		}).join(', ');
		const period_display_info = getSectionPeriodDisplayInfo(section);
		const term_display_info = getTermDisplayInfo(section);

		return {
			...section,
			course_name: (course_info) ? course_info.name : null,
			course_code: (course_info) ? course_info.course_code : null,
			teacher_first_name: teacher_display_info ? teacher_display_info.first_name : null,
			teacher_last_name: teacher_display_info ? teacher_display_info.last_name : null,
			department_sort_text: department_sort_text || 'No department',
			classroom_name: classroom_info ? classroom_info.classroom_name : '',
			period_term_sort_text: `${period_display_info.period_names.join(', ')} ${term_display_info.selected_terms.join(', ')}`.trim(),
			term_quarter_count_sort_value: term_display_info.selected_terms.length,
			term_sequence_sort_text: term_display_info.selected_terms.map(term => `${term}`.padStart(2, '0')).join('-'),
		};
	}, [classroomsById, coursesById, departmentsById, getSectionPeriodDisplayInfo, getTeacherDisplayInfo]);

	const groupedRows = useMemo(() => {
		const logged_spans = new Set();

		return (Array.isArray(sections) ? sections : []).reduce((results, section) => {
			if(!section || section.section_type !== '1' || !hasCourseId(section.course_id)) return results;

			const section_id_key = `${section.section_id}`;
			if(subsectionsById.has(section_id_key)) return results;

			const section_span_id = section.span_id;
			if(section_span_id && logged_spans.has(section_span_id)) return results;
			if(section_span_id) logged_spans.add(section_span_id);

			const subsection_ids = [
				...(Array.isArray(section.subsections) ? section.subsections : []),
				...(subsectionIdsByMainSectionId.get(section_id_key) || []),
			].reduce((results, subsection_id) => {
				if(!results.some(existing_subsection_id => `${existing_subsection_id}` === `${subsection_id}`)) results.push(subsection_id);
				return results;
			}, []);

			const subsection_rows = subsection_ids.reduce((subsection_results, subsection_id) => {
				const subsection_section_info = sectionsById.get(`${subsection_id}`);
				if(!subsection_section_info || subsection_section_info.section_type !== '1' || !hasCourseId(subsection_section_info.course_id)) return subsection_results;

				subsection_results.push(addExtraDataToSection(subsection_section_info));
				return subsection_results;
			}, []);

			const enriched_section = addExtraDataToSection(section);
			const search_parts = [
				enriched_section.course_name,
				enriched_section.course_code,
				enriched_section.teacher_first_name,
				enriched_section.teacher_last_name,
				...subsection_rows.flatMap(subsection_info => [
					subsection_info.course_name,
					subsection_info.course_code,
					subsection_info.teacher_first_name,
					subsection_info.teacher_last_name,
				]),
			].filter(Boolean);

			results.push({
				...enriched_section,
				subsection_rows,
				search_text: search_parts.join(' ').toLowerCase(),
			});
			return results;
		}, []);
	}, [addExtraDataToSection, sections, sectionsById, subsectionIdsByMainSectionId, subsectionsById]);
	const groupedRowsById = useMemo(() => new Map(groupedRows.map(section => [`${section.section_id}`, section])), [groupedRows]);

	const filteredSourceData = handleChangeComplete ? filteredResults : databaseData;

	const visibleMainSectionIds = useMemo(() => new Set((Array.isArray(filteredSourceData) ? filteredSourceData : []).reduce((results, section) => {
		if(!section || section.section_type !== '1' || !hasCourseId(section.course_id)) return results;

		const subsection_info = subsectionsById.get(`${section.section_id}`);
		results.push(subsection_info ? `${subsection_info.section_id}` : `${section.section_id}`);
		return results;
	}, [])), [filteredSourceData, subsectionsById]);

	const displayResults = useMemo(() => {
		const search_value = debouncedSearchValue.trim().toLowerCase();
		let rows_to_display = groupedRows.filter(section => visibleMainSectionIds.has(`${section.section_id}`));

		if(search_value.length > 1)
		{
			rows_to_display = rows_to_display.filter(section => section.search_text.includes(search_value));
		}

		if(sortedBy === 'period_term_sort_text')
		{
			return [...rows_to_display].sort((section_a, section_b) => compareSectionTermSort(section_a, section_b, sortAscDesc));
		}

		const sort_type = sortedBy === 'section_number' ? 'natural' : null;
		return sortArrayOfObjects([...rows_to_display], sortedBy, sort_type, sortAscDesc);
	}, [debouncedSearchValue, groupedRows, sortAscDesc, sortedBy, visibleMainSectionIds]);

	const handleSortClick = (_sortedData, sorted_by, sort_asc_desc) => {
		setSortedBy(sorted_by);
		setSortAscDesc(sort_asc_desc);
	}

	const handleFilterClick = (selectedFilters = {}) => {
		const filtered_data = applySchoolDataFilters(visibleDatabaseData, selectedFilters, schoolDataFilterCategories);
		const active_filters = Object.keys(selectedFilters).some(filter_key => (selectedFilters[filter_key] || []).length > 0);
		const filtered_span_ids = new Set(filtered_data.map(section_info => section_info?.span_id).filter(span_id => span_id));
		const expanded_filtered_data = filtered_span_ids.size === 0
			? filtered_data
			: visibleDatabaseData.filter(section_info => filtered_data.includes(section_info) || (section_info?.span_id && filtered_span_ids.has(section_info.span_id)));

		setFilteredResults([...expanded_filtered_data]);
		setFilterActive(active_filters);
		setHandleChangeComplete(true);
	}
	
	const toggleEditData = useCallback((dataID) => {
		if(dataID)
		{
			const subsection_info = subsectionsById.get(`${dataID}`);
			if(subsection_info)
			{
				dataID = subsection_info.section_id;
			}
		}

		if(showEditDataModal)
		{
			setShowEditDataModal(false);
			return;
		}

		setCurrentDataID(dataID);
		setShowEditDataModal(true);
	}, [showEditDataModal, subsectionsById]);

	const toggleSelectData = useCallback((data_id) => {
		setSelectedData(prev_selected_data => {
			const data_id_string = `${data_id}`;
			return prev_selected_data.includes(data_id_string)
				? prev_selected_data.filter(selected_id => selected_id !== data_id_string)
				: [...prev_selected_data, data_id_string];
		});
	}, []);
	const visibleSectionIDs = useMemo(() => displayResults.map(section => `${section.section_id}`), [displayResults]);
	const visibleSelectedSectionCount = useMemo(() => {
		const selected_section_ids = new Set(selectedData.map(section_id => `${section_id}`));
		return visibleSectionIDs.filter(section_id => selected_section_ids.has(section_id)).length;
	}, [selectedData, visibleSectionIDs]);
	const allVisibleSectionsSelected = visibleSectionIDs.length > 0 && visibleSelectedSectionCount === visibleSectionIDs.length;

	const toggleSelectAll = useCallback(() => {
		const visible_section_ids = new Set(visibleSectionIDs);
		setSelectedData(current_selected_data => allVisibleSectionsSelected
			? current_selected_data.filter(section_id => !visible_section_ids.has(`${section_id}`))
			: [...new Set([...current_selected_data.map(section_id => `${section_id}`), ...visibleSectionIDs])]
		);
	}, [allVisibleSectionsSelected, visibleSectionIDs]);

	const getSortedSectionLabels = useCallback((section_info) => {
		if(!section_info || !Array.isArray(section_info.labels)) return [];
		const section_labels = section_info.labels.map(label_id => labelsById.get(`${label_id}`)).filter(label => label);
		return sortArrayOfObjects(section_labels, 'label', 'text', 'asc');
	}, [labelsById]);

	const updateLockSections = useCallback(async (section_ids, locked) => {
		if(!canLockSections) return;
		const currently_locking_section_ids = new Set(lockingSectionIDs.map(section_id => `${section_id}`));
		const normalized_section_ids = [...new Set((Array.isArray(section_ids) ? section_ids : [section_ids])
			.map(section_id => `${section_id}`)
			.filter(section_id => section_id && !currently_locking_section_ids.has(section_id)))];
		if(normalized_section_ids.length === 0) return;
		const requested_section_ids = new Set(normalized_section_ids);

		setLockingSectionIDs(current_ids => [...new Set([...current_ids.map(section_id => `${section_id}`), ...normalized_section_ids])]);

		const data = {school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, section_ids:normalized_section_ids, locked:locked};
		
		const apiName = process.env.REACT_APP_ENDPOINT_NAME;
		const url = '/update-lock-section';
	    const myInit = { // OPTIONAL
	        response: true,
	        body: JSON.stringify(data),
	    };
	   
		try {
			const response = await API.post(apiName, url, myInit);
			const affected_sections = Array.isArray(response?.data?.affected_sections) ? response.data.affected_sections : normalized_section_ids;
			const affected_section_ids = new Set(affected_sections.map(section_id => `${section_id}`));
			setFilteredResults(current_results => current_results.map(section => affected_section_ids.has(`${section.section_id}`) ? {...section, locked} : section));
			setSections(current_sections => current_sections.map(section => affected_section_ids.has(`${section.section_id}`) ? {...section, locked} : section));
			return affected_sections;
		} catch(e)
		{
			console.log(e.response);
			return [];
		} finally {
			setLockingSectionIDs(current_ids => current_ids.filter(current_section_id => !requested_section_ids.has(`${current_section_id}`)));
		}
	}, [canLockSections, lockingSectionIDs, schoolInfo.current_schedule_version_id, schoolInfo.school_id, setSections]);
	const toggleLockSection = useCallback((section_id, locked) => updateLockSections([section_id], locked), [updateLockSections]);
	const duplicateSection = useCallback(async (section_id) => {
		if(!canDeleteSections) return;
		const normalized_section_id = `${section_id}`;
		if(duplicatingSectionIDs.includes(normalized_section_id)) return;

		setDuplicatingSectionIDs(current_ids => current_ids.includes(normalized_section_id) ? current_ids : [...current_ids, normalized_section_id]);

		const apiName = process.env.REACT_APP_ENDPOINT_NAME;
		const url = '/admin/duplicate-section';
		const myInit = {
			response: true,
			body: JSON.stringify({
				school_id: schoolInfo.school_id,
				schedule_version_id: schoolInfo.current_schedule_version_id,
				section_id,
				copy_periods: false,
			}),
		};

		try {
			const response = await API.post(apiName, url, myInit);
			const response_data = response?.data || response;
			const sections_to_return = Array.isArray(response_data?.sections_to_return) ? response_data.sections_to_return : [];
			const subsections_to_return = Array.isArray(response_data?.subsections_to_return) ? response_data.subsections_to_return : [];
			if(sections_to_return.length === 0) throw new Error(response_data?.msg || response_data?.message || 'No duplicated section was returned.');

			setSections(current_sections => [...current_sections, ...sections_to_return]);
			if(subsections_to_return.length > 0 && typeof setSubsections === 'function')
			{
				setSubsections(current_subsections => [...current_subsections, ...subsections_to_return]);
			}
		} catch(error)
		{
			swal({
				title: 'Unable to Duplicate',
				text: error?.response?.data?.msg || error?.response?.data?.message || error?.message || 'There was a problem duplicating this section.',
				icon: 'warning',
			});
			console.log(error?.response || error);
		} finally {
			setDuplicatingSectionIDs(current_ids => current_ids.filter(current_section_id => `${current_section_id}` !== normalized_section_id));
		}
	}, [canDeleteSections, duplicatingSectionIDs, schoolInfo.current_schedule_version_id, schoolInfo.school_id, setSections, setSubsections]);

	const selectedUnlockedSectionIDs = useMemo(() => selectedData.filter(section_id => {
		const section_info = groupedRowsById.get(`${section_id}`);
		return section_info && !isSectionLocked(section_info);
	}), [groupedRowsById, selectedData]);
	const selectedLockedSectionIDs = useMemo(() => selectedData.filter(section_id => isSectionLocked(groupedRowsById.get(`${section_id}`))), [groupedRowsById, selectedData]);
	const selectedSectionsAreUpdatingLocks = useMemo(() => {
		const locking_section_ids = new Set(lockingSectionIDs.map(section_id => `${section_id}`));
		return selectedData.some(section_id => locking_section_ids.has(`${section_id}`));
	}, [lockingSectionIDs, selectedData]);
	const updateSelectedSectionLocks = useCallback(async (locked) => {
		if(!canLockSections || selectedSectionsAreUpdatingLocks) return;
		const section_ids_to_update = locked === '1' ? selectedUnlockedSectionIDs : selectedLockedSectionIDs;
		if(section_ids_to_update.length === 0) return;

		await updateLockSections(section_ids_to_update, locked);
	}, [canLockSections, selectedLockedSectionIDs, selectedSectionsAreUpdatingLocks, selectedUnlockedSectionIDs, updateLockSections]);
	
	const handeDeleteData = useCallback(async (data_ids) => {
		if(!canDeleteSections) return;

		const normalized_data_ids = (Array.isArray(data_ids) ? data_ids : [data_ids])
			.map(data_id => `${data_id}`)
			.filter(data_id => {
				const section_info = groupedRowsById.get(data_id);
				return section_info && !isSectionLocked(section_info);
			});
		if(normalized_data_ids.length === 0) return;

		let delete_data_bool = true;
		const sections_to_delete = normalized_data_ids.map(section_id => groupedRows.find(section => `${section.section_id}` === `${section_id}`)).filter(section => section);
		
		const options =  {
			title: "Are you sure?",
			icon: "warning",
			dangerMode: true,
			buttons: {
				cancel: {
					text: "Cancel",
					value: false,
					visible: true,
					className: 'gray-btn'
				},
				confirm: {
					text: "Yes",
					value: true,
					visible: true,
					className: 'red-btn'
				},
			},
			content: (
				<div>
					<div>
						{normalized_data_ids.length === 1 ?
							(
								<p>Do you really want to delete Section {(sections_to_delete[0] && ('section_number' in sections_to_delete[0])) && sections_to_delete[0].section_number} of {(sections_to_delete[0] && ('course_name' in sections_to_delete[0])) && sections_to_delete[0].course_name} ({(sections_to_delete[0] && ('course_code' in sections_to_delete[0])) && sections_to_delete[0].course_code})?</p>
							):
							(
								<>
									<p>Do you really want to delete the following sections:</p>
									<div style={{marginLeft:'50px', textAlign:'left'}}>
										{sections_to_delete.map((section_info, index) => (
											<div key={index}>
												Section {section_info.section_number || '--'} of {section_info.course_name} ({section_info.course_code})
											</div>
										))}
									</div>
								</>
							)
						}
						<p className='red-text'>This will <strong>permanently</strong> remove them from the database.</p>
					</div>
				</div>
			)
		}
	
		delete_data_bool = await swal(options);
		
		if(delete_data_bool)
		{
			const deleting_section_ids = new Set(normalized_data_ids);
			setDeletingSectionIDs(current_ids => [...new Set([...current_ids.map(section_id => `${section_id}`), ...normalized_data_ids])]);

			const data = {
				school_id: schoolInfo.school_id,
				schedule_version_id: schoolInfo.current_schedule_version_id,
				section_ids: normalized_data_ids
			};
			try
			{
				const response_data = await deleteSection(data);
				const deleted_sections_array = (response_data && Array.isArray(response_data.deleted_sections))
					? response_data.deleted_sections.map(deleted_section_id => `${deleted_section_id}`)
					: [];

				if(deleted_sections_array.length > 0)
				{
					const deleted_sections_set = new Set(deleted_sections_array);
					const new_filtered_results = filteredResults.filter(section => !deleted_sections_set.has(`${section.section_id}`));
					const new_sections = sections.filter(section => !deleted_sections_set.has(`${section.section_id}`));
					const new_subsections = subsections.filter(subsection => !deleted_sections_set.has(`${subsection.section_id}`) && !deleted_sections_set.has(`${subsection.subsection_id}`));

					setFilteredResults(new_filtered_results);
					setSections(new_sections);
					setSubsections(new_subsections);
					setSelectedData([]);

					calculateConflicts({
						school_id: schoolInfo.school_id,
						schedule_version_id: schoolInfo.current_schedule_version_id,
						match_percent: true
					}).then((conflicts_response) => {
						const conflicts_list = conflicts_response.conflicts;
						setConflicts([...conflicts_list]);
						setMatchPercent(conflicts_response.match_percent);
						markConflictDataFresh(schoolInfo.school_id, schoolInfo.current_schedule_version_id, {complete: true});
					});
				}
				else
				{
					swal({
						title: 'Sections Were Not Deleted',
						text: 'These sections could not be deleted. Refresh and try again if needed.',
						icon: 'warning'
					});
				}
			} finally {
				setDeletingSectionIDs(current_ids => current_ids.filter(section_id => !deleting_section_ids.has(`${section_id}`)));
			}
		}
	}, [canDeleteSections, filteredResults, groupedRows, groupedRowsById, schoolInfo.current_schedule_version_id, schoolInfo.school_id, sections, subsections, setSections, setSubsections])
	
	useEffect(() => {
		if(!showEditDataModal) setCurrentDataID(null);
	}, [showEditDataModal]);

	useEffect(() => {
		const deferred_search_timeout = window.setTimeout(() => {
			setDeferredSearchValue(searchValue);
		}, 0);

		return () => window.clearTimeout(deferred_search_timeout);
	}, [searchValue]);

	useEffect(() => {
		if(dataIsLoading || coursesLoading || filterActive) return;
		setFilteredResults([...(Array.isArray(visibleDatabaseData) ? visibleDatabaseData : [])]);
		setHandleChangeComplete(true);
	}, [coursesLoading, dataIsLoading, visibleDatabaseData, filterActive]);
	
	const renderedSectionRows = useMemo(() => {
		if(!handleChangeComplete || coursesLoading) return null;

		return displayResults.map(data_info => {
			const section_id = data_info.section_id;
			const section_is_deleting = deletingSectionIDs.includes(`${section_id}`);
			if(section_is_deleting)
			{
				return (
					<div className={`school-data-sections-row school-data-sections-row-deleting ${showRowControls ? 'school-data-sections-row-with-controls' : 'school-data-sections-row-without-controls'}`} key={section_id} data-sectionid={section_id}>
						<div className='school-data-sections-row-full-loader'>
							<img src={require('../../../images/balls.gif')} alt='deleting...' />
						</div>
					</div>
				);
			}
			const teacher_display_info = getTeacherDisplayInfo(data_info.teacher_id);
			const classroom_info = classroomsById.get(`${data_info.classroom_id}`) || null;
			const secondary_teachers = Array.isArray(data_info.secondary_teachers) ? data_info.secondary_teachers : [];
			const data_departments = ('departments' in data_info) ? data_info.departments : [];
			const department_names = data_departments.length === 0 ? ['No department'] : data_departments.map(department_id => {
				const department_info = departmentsById.get(`${department_id}`);
				return department_info ? department_info.department : 'No department';
			});
			const period_display_info = getSectionPeriodDisplayInfo(data_info);
			const term_display_info = getTermDisplayInfo(data_info);
			const is_inclusion = data_info.is_inclusion === '1';
			const is_locked = isSectionLocked(data_info);
			const section_is_locking = lockingSectionIDs.includes(`${section_id}`);
			const section_is_duplicating = duplicatingSectionIDs.includes(`${section_id}`);
			const text_color = is_inclusion ? 'orange-text' : '';
			const sorted_section_labels = getSortedSectionLabels(data_info);
			const section_subsections = Array.isArray(data_info.subsection_rows) ? data_info.subsection_rows : [];
			const total_num_students = section_subsections.reduce((student_total, subsection_info) => {
				const subsection_students = Array.isArray(subsection_info.student_list) ? subsection_info.student_list.length : 0;
				return student_total + subsection_students;
			}, Array.isArray(data_info.student_list) ? data_info.student_list.length : 0);

			return (
				<React.Fragment key={section_id}>
				<div className={`school-data-sections-row ${showRowControls ? 'school-data-sections-row-with-controls' : 'school-data-sections-row-without-controls'} ${is_locked ? 'school-data-sections-row-locked' : ''}`} data-sectionid={section_id}>
					{showRowControls &&
						<div className='school-data-sections-controls-cell'>
							{(canDeleteSections || canLockSections) ?
								<FontAwesomeIcon className={selectedData.includes(`${section_id}`) ? 'blue-text' : 'dark-gray-text'} icon={selectedData.includes(`${section_id}`) ? faCheckSquare : farSquare} onClick={() => toggleSelectData(section_id)}/>
							:
								<div></div>
							}
							{canLockSections && (section_is_locking ?
								(
									<img src={require('../../../images/balls.gif')} alt='saving...' style={{height:'20px', width:'20px'}}/>
								):
								is_locked ?
								(
									<FontAwesomeIcon className='cursor-pointer bright-yellow-text' icon={faLock} onClick={() => toggleLockSection(section_id, '0')}/>
								):
								(
									<FontAwesomeIcon className='cursor-pointer gray-text' icon={faUnlock} onClick={() => toggleLockSection(section_id, '1')}/>
								)
							)}
						</div>
					}
					<div className='school-data-sections-code-cell'>{data_info.course_code || '--'}</div>
					<div className='school-data-sections-course-cell'>
						<div className='school-data-sections-course-line'>
							<span className={`school-data-sections-course-name ${text_color}`}>{data_info.course_name || 'Unknown Course'}</span>
						</div>
						<div className='school-data-sections-support-line'>
							<span>{teacher_display_info ? teacher_display_info.full_name : '--'}</span>
							{secondary_teachers.map((secondary_teacher_id, i) => {
								const secondary_teacher_info = getTeacherDisplayInfo(secondary_teacher_id);
								if(!secondary_teacher_info) return null;

								return (
									<span key={`${section_id}-secondary-${i}`}>{secondary_teacher_info.full_name}</span>
								)
							})}
						</div>
						<div className='school-data-sections-meta-line'>
							{is_inclusion && <span className='school-data-sections-tag school-data-sections-tag-inclusion'>Inclusion</span>}
							{is_locked && <span className='school-data-sections-tag school-data-sections-tag-locked'><FontAwesomeIcon icon={faLock}/> Locked</span>}
						</div>
						{sorted_section_labels.length > 0 &&
							<div className='school-data-sections-labels-line'>
								{sorted_section_labels.map(label_info => (
									<div className={`mb-section-label extra-small-text ${text_color}`} key={`${section_id}-${label_info.label_id}`}>
										<div className={`mb-section-label-icon ${getLabelColorClass(label_info)}`}></div>
										<div>{label_info.label}</div>
									</div>
								))}
							</div>
						}
					</div>
					<div className='school-data-sections-section-cell'>{data_info.section_number || '--'}</div>
					<div className='school-data-sections-department-cell'>
						{department_names.map((department_name, index) => (
							<div className='school-data-department' key={`${section_id}-department-${index}`}>{department_name}</div>
						))}
					</div>
					<div className='school-data-sections-room-cell'>{classroom_info ? classroom_info.classroom_name : '--'}</div>
					<div className='school-data-sections-period-term-cell'>
						<div className='school-data-sections-period-text'>
							{period_display_info.period_names.length === 0 ?
								<span className='dark-gray-text'>--</span>
							:
								period_display_info.period_names.join(', ')
							}
						</div>
						<div className='school-data-sections-quarter-line'>
							{term_display_info.selected_terms.length === 0 ?
								<span className='dark-gray-text'>--</span>
							:
								<div className={`mb-section-quarter-day-container mb-section-quarter-day-container-readonly mb-section-quarter-day-container-compact ${term_display_info.display_terms.length > 4 ? 'mb-section-quarter-day-container-cycle' : ''}`}>
									{term_display_info.display_terms.map(term => (
										<div className={`mb-section-quarter-day ${term_display_info.selected_terms.includes(term) ? 'mb-section-quarter-day-selected' : ''}`} key={`${section_id}-term-${term}`}>
											{term_display_info.term_prefix}{term}
										</div>
									))}
								</div>
							}
						</div>
					</div>
					<div className='school-data-sections-roster-cell'><FontAwesomeIcon icon={faUserGraduate}/> {total_num_students}</div>
					{is_locked ?
						(
							<div className='school-data-sections-actions-cell'></div>
						):
						(
						<div className={`school-data-sections-actions-cell ${!canDeleteSections ? 'school-data-sections-actions-cell-single' : ''}`}>
							<FontAwesomeIcon className='gray-to-dark-blue-link' icon={faEdit} onClick={() => toggleEditData(section_id)}/>
							{canDeleteSections && (section_is_duplicating ?
								<img src={require('../../../images/balls.gif')} alt='duplicating...' className='school-data-sections-action-loading'/>
							:
								<FontAwesomeIcon className='gray-to-dark-blue-link' icon={faClone} onClick={() => duplicateSection(section_id)}/>
							)}
							{canDeleteSections && <FontAwesomeIcon className='gray-to-red-link' icon={faTrashAlt} onClick={() => handeDeleteData(section_id)}/>}
						</div>
						)
					}
				</div>
				{section_subsections.map(subsection_info => {
					const subsection_id = subsection_info.section_id;
					const subsection_is_inclusion = subsection_info.is_inclusion === '1';
					const subsection_text_color = subsection_is_inclusion ? 'orange-text' : 'blue-text';
					const subsection_teacher_display_info = getTeacherDisplayInfo(subsection_info.teacher_id);
					const subsection_secondary_teachers = Array.isArray(subsection_info.secondary_teachers) ? subsection_info.secondary_teachers : [];
					const subsection_labels = getSortedSectionLabels(subsection_info);
					const subsection_student_count = Array.isArray(subsection_info.student_list) ? subsection_info.student_list.length : 0;

					return (
						<div className={`school-data-sections-row school-data-sections-subsection-row ${showRowControls ? 'school-data-sections-row-with-controls' : 'school-data-sections-row-without-controls'}`} key={`${section_id}-${subsection_id}`} data-sectionid={subsection_id}>
							{showRowControls && <div></div>}
							<div></div>
							<div className='school-data-sections-course-cell'>
								<div className='small-text'>
									<span className={subsection_text_color}>{subsection_is_inclusion ? 'Inclusion Subsection' : 'Subsection'}</span>
								</div>
								<div className={subsection_text_color}><b>{subsection_info.course_name}</b></div>
								{subsection_teacher_display_info &&
									<div className={`medium-text ${subsection_text_color}`}>{subsection_teacher_display_info.full_name}</div>
								}
								{subsection_secondary_teachers.map((secondary_teacher_id, i) => {
									const secondary_teacher_info = getTeacherDisplayInfo(secondary_teacher_id);
									if(!secondary_teacher_info) return null;

									return (
										<div className={`medium-text ${subsection_text_color}`} key={`${subsection_id}-secondary-${i}`}>{secondary_teacher_info.full_name}</div>
									)
								})}
								{subsection_labels.map(label_info => (
									<div className={`mb-section-label extra-small-text ${subsection_text_color}`} key={`${subsection_id}-${label_info.label_id}`}>
										<div className={`mb-section-label-icon ${getLabelColorClass(label_info)}`}></div>
										<div>{label_info.label}</div>
									</div>
								))}
							</div>
							<div className='school-data-sections-section-cell'>{subsection_info.section_number || '--'}</div>
							<div className='school-data-sections-department-cell'></div>
							<div className='school-data-sections-room-cell'></div>
							<div className='school-data-sections-period-term-cell'></div>
							<div className='school-data-sections-roster-cell'><FontAwesomeIcon icon={faUserGraduate}/> {subsection_student_count}</div>
							<div></div>
						</div>
					);
				})}
				</React.Fragment>
			)
		});
	}, [canDeleteSections, canLockSections, classroomsById, coursesLoading, deletingSectionIDs, departmentsById, displayResults, duplicateSection, duplicatingSectionIDs, getSectionPeriodDisplayInfo, getSortedSectionLabels, getTeacherDisplayInfo, handleChangeComplete, handeDeleteData, lockingSectionIDs, selectedData, showRowControls, toggleEditData, toggleLockSection, toggleSelectData]);

	// console.log('displayResults', displayResults)

	return (
		<>
		{showEditDataModal &&
			<EditSection schoolInfo={schoolInfo} currentView={null} currentDay={'1'} currentViewType={null} scheduleType={scheduleInfo.schedule_type} sectionType='1' sectionID={currentDataID} dataID={null} toggleEditSection={toggleEditData} scheduleInfo={scheduleInfo} scheduleStructureData={scheduleStructureData} lunchInfo={lunchInfo} classrooms={classrooms} courses={courses} setCourses={setCourses} teachers={teachers} setTeachers={setTeachers} students={students} setStudents={setStudents} sections={sections} setSections={setSections} subsections={subsections} setSubsections={setSubsections} labels={labels} setLabels={setLabels} conflicts={[]} setConflicts={()=>{}} setConflictsLoading={()=>{}} setMatchPercent={()=>{}} setMatchPercentLoading={()=>{}} toggleEditStudentSchedule={()=>{}} allow_hotlinks={false} filteredResults={filteredResults} setFilteredResults={setFilteredResults} studentOnlyEditing={studentOnlySectionEditing}/>
		}
		<div className='school-data-content-screen'>
			<h1 className='school-data-main-heading capitalize'>Sections</h1>
			<div className='school-data-search-filter-row'>
				<div className='school-data-search-bar-container'>
					<input className='school-data-search-bar' placeholder="Search for a section by course code, course name, or teacher" value={searchValue} onChange={(e) => setSearchValue(e.target.value)} />
					<FontAwesomeIcon className='school-data-search-bar-icon' icon={faSearch}/>
				</div>
				<SchoolDataFilter categories={schoolDataFilterCategories} onChange={handleFilterClick} resultCount={displayResults.length}/>
			</div>
			<div className='school-data-database-display'>
				<div className={`school-data-sections-row school-data-sections-header ${showRowControls ? 'school-data-sections-row-with-controls' : 'school-data-sections-row-without-controls'} fixed-heading-on-scroll`}>
					{showRowControls && <div></div>}
					<div className='school-data-database-header-col'>Code <SortToggle handleOnClick={handleSortClick} sortKey='course_code' passedData={displayResults}/></div>
					<div className='school-data-database-header-col'>Course <SortToggle handleOnClick={handleSortClick} sortKey='course_name' passedData={displayResults}/></div>
					<div className='school-data-database-header-col school-data-sections-header-centered'>Section <SortToggle handleOnClick={handleSortClick} sortKey='section_number' passedData={displayResults}/></div>
					<div className='school-data-database-header-col school-data-sections-department-header'>Department <SortToggle handleOnClick={handleSortClick} sortKey='department_sort_text' passedData={displayResults}/></div>
					<div className='school-data-database-header-col school-data-sections-room-header'>Room <SortToggle handleOnClick={handleSortClick} sortKey='classroom_name' passedData={displayResults}/></div>
					<div className='school-data-database-header-col school-data-sections-period-term-header'>Period / Term <SortToggle handleOnClick={handleSortClick} sortKey='period_term_sort_text' passedData={displayResults}/></div>
					<div className='school-data-sections-header-centered'># Students</div>
					{!readOnlyAddUpload ?
						<div className='school-data-database-add-new-container' onClick={() => toggleEditData(null)}>
							<div className='school-data-database-add-new-btn'>
								<FontAwesomeIcon className='white-text' icon={faPlus}/>
							</div>
						</div>
					:
						<div></div>
					}
				</div>
				{displayResults.length > 0 && showRowControls &&
					<div className='select-all-row school-data-sections-select-all-row'>
						<div className={`select-all-container ${visibleSelectedSectionCount > 0 && 'blue-text'}`} onClick={toggleSelectAll}>
							<FontAwesomeIcon icon={allVisibleSectionsSelected ? faCheckSquare : visibleSelectedSectionCount > 0 ? faMinusSquare : farSquare}/>
							<FontAwesomeIcon className='select-all-arrow' icon={faSortDown}/>
						</div>
						<div className='school-data-sections-bulk-selection-summary'>
							<div className='small-text school-data-sections-selected-count'>{selectedData.length} selected</div>
							{selectedData.length > 0 &&
								<div className='select-all-actions-container school-data-sections-bulk-actions'>
									{canLockSections &&
										<div className='tooltip' data-tooltip-text='Lock selected sections' onClick={() => selectedUnlockedSectionIDs.length === 0 || selectedSectionsAreUpdatingLocks ? null : updateSelectedSectionLocks('1')}>
											<FontAwesomeIcon className={`select-all-action-btn tooltip ${selectedUnlockedSectionIDs.length === 0 || selectedSectionsAreUpdatingLocks ? 'disabled' : ''}`} icon={faLock}/>
										</div>
									}
									{canLockSections &&
										<div className='tooltip' data-tooltip-text='Unlock selected sections' onClick={() => selectedLockedSectionIDs.length === 0 || selectedSectionsAreUpdatingLocks ? null : updateSelectedSectionLocks('0')}>
											<FontAwesomeIcon className={`select-all-action-btn tooltip ${selectedLockedSectionIDs.length === 0 || selectedSectionsAreUpdatingLocks ? 'disabled' : ''}`} icon={faUnlock}/>
										</div>
									}
									{canDeleteSections &&
										<div className='tooltip' data-tooltip-text={selectedSectionsAreUpdatingLocks ? 'Wait for lock updates to finish' : selectedUnlockedSectionIDs.length > 0 ? 'Delete selected unlocked sections' : 'Unlock selected sections before deleting'} onClick={() => selectedUnlockedSectionIDs.length === 0 || selectedSectionsAreUpdatingLocks ? null : handeDeleteData(selectedData)}>
											<FontAwesomeIcon className={`select-all-action-btn tooltip ${selectedUnlockedSectionIDs.length === 0 || selectedSectionsAreUpdatingLocks ? 'disabled' : ''}`} icon={faTrashAlt}/>
										</div>
									}
								</div>
							}
						</div>
					</div>
				}
				{(!handleChangeComplete || coursesLoading || dataIsLoading) ?
				(
					<div className='school-data-database-message-container'>
						<img src={require('../../../images/balls.gif')} alt='loading...' style={{height:'80px'}}/>
					</div>
				) :
				(
					<>
					{displayResults.length === 0 && handleChangeComplete && !coursesLoading && !dataIsLoading &&
						<div className='school-data-database-message-container'>
							<h4>Sorry, no results were found!</h4>
						</div>
					}
					{Object.keys(scheduleStructureData).length > 0 &&
						<>
						{renderedSectionRows}
						</>
					}
					</>
				)}
			</div>
		</div>
		</>
	);
}
